import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, eur } from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";

const STATUS_LABEL = {
  approved: { text: "Attivo", cls: "bg-pine/10 text-pine border-pine/25" },
  pending: { text: "In attesa", cls: "bg-gold/15 text-ink border-gold/50" },
  rejected: { text: "Non approvato", cls: "bg-bordeaux/10 text-bordeaux border-bordeaux/25" },
};

function FinancialEditor({ user, onSaved }) {
  const [form, setForm] = useState({
    capitale: user.capitale ?? 0,
    totale_ritirato: user.totale_ritirato ?? 0,
    totale_profitto: user.totale_profitto ?? 0,
    data_inizio: user.data_inizio ? user.data_inizio.slice(0, 10) : "",
    riconoscimento_presentatore: user.riconoscimento_presentatore ?? 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/financials`, {
        capitale: Number(form.capitale) || 0,
        totale_ritirato: Number(form.totale_ritirato) || 0,
        totale_profitto: Number(form.totale_profitto) || 0,
        data_inizio: form.data_inizio || null,
        riconoscimento_presentatore: Number(form.riconoscimento_presentatore) || 0,
      });
      toast.success(`Dati aggiornati per ${user.nome} ${user.cognome}.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = "number") => (
    <label className="block">
      <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ink-muted">{label}</span>
      <input
        type={type}
        className="input-field !py-2 mt-1 !text-sm"
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        data-testid={`admin-fin-${key}-${user.id.slice(0, 6)}`}
      />
    </label>
  );

  return (
    <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
      {field("capitale", "Capitale €")}
      {field("totale_profitto", "Profitto €")}
      {field("totale_ritirato", "Ritirato €")}
      {field("riconoscimento_presentatore", "Riconoscimento al presentatore €")}
      {field("data_inizio", "Data inizio", "date")}
      <button onClick={save} disabled={busy} className="btn-outline !px-4 !py-2.5 disabled:opacity-60" data-testid={`admin-fin-save-${user.id.slice(0, 6)}`}>
        {busy ? "Salvataggio…" : "Salva"}
      </button>
    </div>
  );
}

function PerformanceEditor({ user, onSaved }) {
  const [rows, setRows] = useState(() =>
    (user.performance || []).map((p) => ({ mese: p.mese, capitale: p.capitale }))
  );
  const [busy, setBusy] = useState(false);

  const setRow = (i, key, value) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));

  const save = async () => {
    const entries = rows
      .filter((r) => r.mese)
      .map((r) => ({ mese: r.mese, capitale: Number(r.capitale) || 0 }));
    setBusy(true);
    try {
      await api.put(`/admin/users/${user.id}/performance`, { entries });
      toast.success(`Storico aggiornato per ${user.nome} ${user.cognome}.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-t border-pine/10 pt-5" data-testid={`admin-perf-${user.id.slice(0, 6)}`}>
      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ink-muted mb-3">
        Storico mensile del capitale (grafico area cliente)
      </p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
            <input
              type="month"
              className="input-field !py-2 !text-sm"
              value={r.mese}
              onChange={(e) => setRow(i, "mese", e.target.value)}
              data-testid={`admin-perf-mese-${user.id.slice(0, 6)}-${i}`}
            />
            <input
              type="number"
              min="0"
              className="input-field !py-2 !text-sm"
              value={r.capitale}
              onChange={(e) => setRow(i, "capitale", e.target.value)}
              placeholder="Capitale €"
              data-testid={`admin-perf-capitale-${user.id.slice(0, 6)}-${i}`}
            />
            <button
              onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
              className="p-2 text-bordeaux hover:bg-bordeaux/10 transition-colors"
              aria-label="Rimuovi mese"
              data-testid={`admin-perf-del-${user.id.slice(0, 6)}-${i}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => setRows((rs) => [...rs, { mese: "", capitale: "" }])}
          className="btn-outline !px-4 !py-2"
          data-testid={`admin-perf-add-${user.id.slice(0, 6)}`}
        >
          <Plus size={14} /> Aggiungi mese
        </button>
        <button onClick={save} disabled={busy} className="btn-primary !px-4 !py-2 disabled:opacity-60" data-testid={`admin-perf-save-${user.id.slice(0, 6)}`}>
          {busy ? "Salvataggio…" : "Salva storico"}
        </button>
      </div>
    </div>
  );
}

function ContractUploader({ user, onSaved }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/users/${user.id}/contract`, fd);
      toast.success(`Contratto caricato per ${user.nome} ${user.cognome}.`);
      setFile(null);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Caricamento non riuscito."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-t border-pine/10 pt-5" data-testid={`admin-contract-${user.id.slice(0, 6)}`}>
      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ink-muted mb-3">
        Contratto PDF (scaricabile dall'area personale del cliente)
      </p>
      {user.contract && (
        <p className="mb-3 text-sm text-ink-muted" data-testid={`admin-contract-current-${user.id.slice(0, 6)}`}>
          Attuale: <span className="text-ink font-medium">{user.contract.filename}</span>
          {" · "}caricato il {(user.contract.created_at || "").slice(0, 10).split("-").reverse().join("/")}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm text-ink-muted file:mr-3 file:border file:border-pine/30 file:bg-cream file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-[0.15em] file:text-pine"
          data-testid={`admin-contract-file-${user.id.slice(0, 6)}`}
        />
        <button
          onClick={upload}
          disabled={!file || busy}
          className="btn-outline !px-4 !py-2 disabled:opacity-60"
          data-testid={`admin-contract-save-${user.id.slice(0, 6)}`}
        >
          {busy ? "Caricamento…" : user.contract ? "Sostituisci contratto" : "Carica contratto"}
        </button>
      </div>
    </div>
  );
}


function ImportCard({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/performance", fd);
      setResult(data);
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err, "Importazione non riuscita."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-6 sm:p-8 mt-6" data-testid="admin-import-card">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">Aggiornamento massivo storici</p>
      <p className="mt-3 text-sm text-ink-muted leading-relaxed max-w-2xl">
        Carica un file CSV con colonne <code className="text-ink">email, mese, capitale</code> (mese in formato
        AAAA-MM, es. 2026-09). Ogni riga aggiorna lo storico mensile del cliente corrispondente e il cliente
        riceve una notifica via email.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm text-ink-muted file:mr-4 file:px-4 file:py-2.5 file:border file:border-pine/40 file:bg-transparent file:text-pine file:text-xs file:font-semibold file:uppercase file:tracking-[0.15em] file:cursor-pointer hover:file:bg-pine hover:file:text-cream file:transition-colors"
          data-testid="admin-import-file"
        />
        <button onClick={upload} disabled={!file || busy} className="btn-primary !px-5 !py-2.5 disabled:opacity-60 shrink-0" data-testid="admin-import-submit">
          {busy ? "Caricamento…" : "Carica e aggiorna"}
        </button>
      </div>
      {result && (
        <div className="mt-5 border-t border-pine/10 pt-4" data-testid="admin-import-result">
          <p className="text-sm text-ink">{result.message}</p>
          {result.errori?.length > 0 && (
            <ul className="mt-3 space-y-1">
              {result.errori.map((e, i) => (
                <li key={i} className="text-xs text-bordeaux">{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, r, u, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/registrations", { params: { status: "pending" } }),
        api.get("/admin/users"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setPending(r.data.users);
      setClients(u.data.users);
      setContacts(c.data.contacts);
    } catch (err) {
      toast.error(formatApiError(err, "Impossibile caricare i dati amministrativi."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/registrations/${id}/${action}`);
      toast.success(action === "approve" ? "Richiesta approvata." : "Richiesta non approvata.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((u) =>
      `${u.nome} ${u.cognome} ${u.email}`.toLowerCase().includes(q)
    );
  }, [clients, query]);

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 py-24 text-ink-muted" data-testid="admin-loading">Caricamento…</div>;
  }

  const kpis = stats
    ? [
        { label: "Clienti attivi", value: String(stats.clienti_attivi), testid: "kpi-attivi" },
        { label: "Richieste in attesa", value: String(stats.richieste_in_attesa), testid: "kpi-attesa" },
        { label: "Capitale complessivo", value: eur(stats.capitale_totale), testid: "kpi-capitale" },
        { label: "Profitto complessivo", value: eur(stats.profitto_totale), testid: "kpi-profitto" },
        { label: "Richieste di contatto", value: String(stats.contatti_totali), testid: "kpi-contatti" },
      ]
    : [];

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24 space-y-20" data-testid="admin-page">
      <section>
        <Eyebrow>Area aziendale</Eyebrow>
        <h1 className="mt-4 text-4xl sm:text-5xl leading-tight" data-testid="admin-title">
          La struttura in <em className="italic text-gold">sintesi</em>.
        </h1>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-px bg-pine/10 border border-pine/10" data-testid="admin-kpis">
          {kpis.map((k) => (
            <div key={k.label} className="bg-surface p-6">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ink-muted">{k.label}</p>
              <p className="mt-2 font-serif text-2xl sm:text-3xl text-ink" data-testid={k.testid}>{k.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <Eyebrow>Richieste di accesso</Eyebrow>
        {pending.length === 0 ? (
          <p className="mt-6 text-ink-muted" data-testid="admin-no-pending">Nessuna richiesta in attesa.</p>
        ) : (
          <div className="mt-6 space-y-4">
            {pending.map((u) => (
              <Reveal key={u.id}>
                <div className="card-surface p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6 justify-between" data-testid={`admin-pending-${u.id.slice(0, 6)}`}>
                  <div>
                    <p className="font-serif text-2xl text-ink">{u.nome} {u.cognome}</p>
                    <p className="text-sm text-ink-muted mt-1">{u.email} · {u.telefono || "—"}</p>
                    <p className="text-xs text-ink-muted mt-1">
                      Presentato da: {u.referred_by || "—"} · Codice assegnato: {u.referral_code}
                    </p>
                    <p className="mt-2">
                      <span className={`text-[0.6rem] font-bold uppercase tracking-[0.2em] px-2.5 py-1 border ${
                        u.email_verified
                          ? "bg-pine/10 text-pine border-pine/25"
                          : "bg-bordeaux/10 text-bordeaux border-bordeaux/25"
                      }`} data-testid={`admin-verified-${u.id.slice(0, 6)}`}>
                        {u.email_verified ? "Email verificata" : "Email non verificata"}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-3 shrink-0">
                    <button onClick={() => act(u.id, "approve")} className="btn-primary !px-5 !py-2.5" data-testid={`admin-approve-${u.id.slice(0, 6)}`}>
                      Approva
                    </button>
                    <button onClick={() => act(u.id, "reject")} className="btn-outline !px-5 !py-2.5" data-testid={`admin-reject-${u.id.slice(0, 6)}`}>
                      Non approvare
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Eyebrow>Clienti</Eyebrow>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted" aria-hidden="true" />
              <input
                className="input-field !py-2.5 !pl-10 !text-sm w-64"
                placeholder="Cerca nome o email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="admin-search"
                aria-label="Cerca cliente"
              />
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/clients.csv`}
              className="btn-outline !px-4 !py-2.5"
              data-testid="admin-export-clients"
            >
              <Download size={14} /> Clienti CSV
            </a>
          </div>
        </div>
        <ImportCard onDone={load} />
        {filtered.length === 0 ? (
          <p className="mt-8 text-ink-muted" data-testid="admin-no-clients">Nessun cliente trovato.</p>
        ) : (
          <div className="mt-8 space-y-4">
            {filtered.map((u) => {
              const st = STATUS_LABEL[u.status] || STATUS_LABEL.pending;
              return (
                <div key={u.id} className="card-surface p-6 sm:p-8" data-testid={`admin-client-${u.id.slice(0, 6)}`}>
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                    <p className="font-serif text-2xl text-ink">{u.nome} {u.cognome}</p>
                    <span className={`text-[0.6rem] font-bold uppercase tracking-[0.2em] px-2.5 py-1 border ${st.cls}`} data-testid={`admin-status-${u.id.slice(0, 6)}`}>
                      {st.text}
                    </span>
                  </div>
                  <p className="text-sm text-ink-muted mt-1 mb-5">
                    {u.email} · {u.telefono || "—"} · Codice: {u.referral_code} · Presentato da: {u.referred_by || "—"}
                  </p>
                  {u.status === "approved" && (
                    <>
                      <FinancialEditor user={u} onSaved={load} />
                      <PerformanceEditor user={u} onSaved={load} />
                      <ContractUploader user={u} onSaved={load} />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Contatti</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl leading-tight" data-testid="admin-contacts-title">
              Richieste <em className="italic text-gold">ricevute</em>.
            </h2>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/contacts.csv`}
            className="btn-outline !px-4 !py-2.5"
            data-testid="admin-export-contacts"
          >
            <Download size={14} /> Contatti CSV
          </a>
        </div>
        {contacts.length === 0 ? (
          <p className="mt-8 text-ink-muted" data-testid="admin-no-contacts">Nessuna richiesta di contatto.</p>
        ) : (
          <div className="mt-8 space-y-4">
            {contacts.map((c) => (
              <div key={c.id} className="card-surface p-6 sm:p-8" data-testid={`admin-contact-${c.id.slice(0, 6)}`}>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <p className="font-serif text-2xl text-ink">{c.nome}</p>
                  <span className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-pine bg-pine-soft px-3 py-1">{c.argomento}</span>
                </div>
                <p className="text-sm text-ink-muted mt-1">{c.email} · {c.telefono || "—"} · {c.paese}</p>
                <p className="mt-4 text-base text-ink leading-relaxed whitespace-pre-wrap">{c.messaggio}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
